import { forwardRef } from 'react';
// eslint-disable-next-line no-restricted-imports -- this is the single sanctioned wrapper around the library
import BaseCurrencyInput, { CurrencyInputProps, formatValue } from 'react-currency-input-field';

export type { CurrencyInputProps };
export { formatValue };

/**
 * Default cap on the number of characters in the cleaned numeric value (digits + decimal point).
 *
 * `react-currency-input-field` rejects a keystroke (and a paste) once the cleaned value exceeds
 * `maxLength`, so this stops the user entering an amount large enough to break the backend.
 *
 * 13 characters caps entry at ~$9.99 trillion in whole dollars (13 digits), or ~$9.99 billion with
 * cents (10 digits + ".99"). Crucially, even the worst case — 13 digits as whole dollars — stays a
 * safe integer once converted to cents (9,999,999,999,999 × 100 ≈ 1e15, below Number.MAX_SAFE_INTEGER
 * ≈ 9e15), so the input can never produce a value that loses JS precision or overflows the backend
 * `Long`. Save boundaries additionally apply a `Number.isSafeInteger` guard as defense-in-depth (ELE-726).
 */
export const CURRENCY_INPUT_MAX_LENGTH = 13;

/**
 * Shared wrapper around `react-currency-input-field`.
 *
 * Defaults `disableAbbreviations` on so a trailing letter is NOT parsed as a magnitude
 * multiplier (`k` → ×1,000, `m` → ×1,000,000, `b` → ×1,000,000,000). Without this, a stray
 * letter — e.g. the `m` in "5,000/month", or a typed "5b" — silently multiplies the amount
 * (ELE-726). Pass `disableAbbreviations={false}` to opt back in for a specific field.
 *
 * All currency/number/percentage inputs must render this wrapper instead of importing
 * `react-currency-input-field` directly (enforced by the `no-restricted-imports` lint rule).
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>((props, ref) => (
  <BaseCurrencyInput ref={ref} disableAbbreviations maxLength={CURRENCY_INPUT_MAX_LENGTH} {...props} />
));
